import React from 'react';
import Dropzone, { DropEvent, FileRejection } from 'react-dropzone';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BlockIcon from '@mui/icons-material/Block';
import RadarIcon from '@mui/icons-material/Radar';
import RemoveIcon from '@mui/icons-material/Remove';
import MenuItem from '@mui/material/MenuItem';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import { Select, FormControl, Tooltip } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

import { CommentOrReport, createCommentOrReport } from '../../../../store/details/comment-or-report/model';
import { Ii18n } from '../../../../store/utils/contracts/i18n.interface';
import { getI18n } from '../../../../store/utils/i18n/strings.manager';
import styles from './comment-or-report-item.module.css';
import { TypeCodeTranslation } from '../../../../business/models/shared.model';
import { getTypeCodesForLanguage } from '../../../../store/utils/typecodes/typecode-translations';
import { ReportStatusTC } from '../../../../business/models/typecode.enums';

type FileWithPreview = File & {
  preview: string;
};

interface Props {
  readonly filename: string;
  readonly description?: string;
  readonly subjectLine?: string;
  readonly url: string;
  readonly content: string;
  readonly amazonKey: string;
  readonly isOnline?: boolean;
  readonly onSubjectChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  readonly onDescriptionChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  readonly onRemoveClick: React.MouseEventHandler;
  readonly onUploadClick: React.MouseEventHandler;
  readonly onAttachClick: (attachment: CommentOrReport) => any;
  readonly reportStatusTC: string;
  readonly reportStatusTCValues: TypeCodeTranslation[];
  readonly onDocumentTypeChanged: (event: SelectChangeEvent<string>, child: React.ReactNode) => void;
  readonly handleDownloadClick: (amazonKey: string, filename: string) => void; // Handle independently
  readonly disabled: boolean;
  readonly currentLanguage: string;
  readonly VirusDetected?: boolean;
  readonly AVScanned?: boolean;
}

export class CommentOrReportItem extends React.Component<Props> {
  private localization: Ii18n;
  private dropzoneRef: any;

  constructor(props: Props) {
    super(props);
    this.localization = getI18n();
  }

  private async onFilesDropped(files: FileWithPreview[]) {
    const commentOrReportPromise = new Promise<CommentOrReport>((resolve) => {
      const commentOrReport = createCommentOrReport();
      commentOrReport.filename = files[0].name;
      commentOrReport.url = files[0].preview || '';
      
      if ((files[0].name.indexOf('.docx') > -1 ||
         files[0].name.indexOf('.doc') > -1 ||
         files[0].name.indexOf('.pdf') > -1 ||
         files[0].name.indexOf('.xls') > -1 ||
         files[0].name.indexOf('.xlsx') > -1 ||
         files[0].name.indexOf('.jpeg') > -1 ||
         files[0].name.indexOf('.jpg') > -1 ||
         files[0].name.indexOf('.txt') > -1) &&
         files[0].type !== 'application/x-msdownload') {
          const fileReader = new FileReader();
            fileReader.onloadend = () => {
              let c = '';
              if (typeof fileReader.result === 'string') {
                c = fileReader.result.split(',')[1] || '';
              } else if (fileReader.result instanceof ArrayBuffer) {
                const uint8Array = new Uint8Array(fileReader.result);
                const binaryString = String.fromCharCode(...uint8Array);
                c = binaryString.split(',')[1] || '';
              }
              const fc = atob(c);
              const fcf = fc.charAt(0) + fc.charAt(1);
              if (fcf !== 'MZ') {
                commentOrReport.content = c;
                resolve(commentOrReport);
                return;
              } else {
                return;
              }
          };
          fileReader.readAsDataURL(files[0]);
         } else {
        return;
      }
    });
    this.props.onAttachClick(await commentOrReportPromise);
  }

  renderDescription = () => {
    if (this.props.reportStatusTC !== ReportStatusTC.FreeText) { return null; } // or undefined, or false

    return (
      <div className={`${styles.itemGrow} ${styles.border}`}>
        <textarea rows={4} className={styles.text}
          value={this.props.description}
          onChange={this.props.onDescriptionChange}
          disabled={this.props.isOnline}
          placeholder={this.localization.get('details.commentOrReport.description')} />
      </div>
    );
  }

  getDescriptionDivDisabled = () => {
    return this.props.reportStatusTC !== ReportStatusTC.FreeText;
  };

  render() {
    const attachIcon = () => {
      if (this.props.VirusDetected) {
        return (
          <Tooltip title="Virus detected">
            <div className={styles.noFileActions}>
              <CoronavirusIcon style={{ fontSize: '4rem', color: '#842200' }} />
            </div>
          </Tooltip>
        );
      } else {
        if (this.props.amazonKey && this.props.reportStatusTC !== ReportStatusTC.FreeText) {
          if (this.props.AVScanned === false) {
            return (
                <Tooltip title="AV Scanning">
                  <div className={styles.noFileActions}>
                    <RadarIcon style={{ fontSize: '4rem', color: '#842200' }} />
                  </div>
                </Tooltip>  
            );
          } else {
            return (
              <div
                className={styles.fileActions}
                onClick={() => {
                  // Handle the non-serializable onClick logic independently
                  this.props.handleDownloadClick(this.props.amazonKey, this.props.filename);
                }}
              >
                <img alt="" src={require('../../../../assets/images/file55x69.png')} />
                <span className={styles.filename}>{this.props.filename}</span>
              </div>
            );
          }
        } else if (!this.props.isOnline && this.attachEnabled) {
          return (
            <div
              className={styles.fileActions}
              onClick={() => {
                this.dropzoneRef.open();
              }}
            >
              <AttachFileIcon style={{ fontSize: '4rem', color: '#84a1d4' }} />
            </div>
          );
        } else {
          if (this.props.AVScanned === false && this.props.reportStatusTC !== ReportStatusTC.FreeText) {
            return (
                <Tooltip title="AV Scanning">
                  <div className={styles.noFileActions}>
                    <RadarIcon style={{ fontSize: '4rem', color: '#842200' }} />
                  </div>
                </Tooltip>  
              );
          } else {
            return (
              <div className={styles.noFileActions}>
                <BlockIcon style={{ fontSize: '4rem', color: '#84a1d4' }} />
              </div>
            );
          }
        }
      }
    };

    const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
      const filesWithPreview = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      ) as FileWithPreview[];
      this.onFilesDropped(filesWithPreview);
    };

    return (
      <Dropzone
        ref={(node: any) => {
          this.dropzoneRef = node;
        }}
        onDrop={handleDrop}
        noClick={true}
      >
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps()} className={styles.dropzone}>
            <input {...getInputProps()} />
            <div className={styles.container}>
              <div className={styles.rowGrow}>
                <div className={styles.columnFile}>
                  {(this.props.reportStatusTC === ReportStatusTC.End ||
                    this.props.reportStatusTC === ReportStatusTC.EndNoSVS ||
                    this.props.reportStatusTC === ReportStatusTC.Temp) &&
                    !this.props.filename && (
                      <div className={styles.notValid}></div>
                    )}
                  <div className={`${styles.itemGrow} ${styles.border}`}>{attachIcon()}</div>
                </div>
                <div className={styles.columnGrow}>
                  <div className={`${styles.item} ${styles.border}`}>
                    <FormControl className={styles.formControl}>
                      <Select
                        className={styles.select}
                        value={this.props.reportStatusTC}
                        onChange={this.props.onDocumentTypeChanged}
                        displayEmpty
                        disabled={this.props.isOnline}
                      >
                        <MenuItem value="" key="" disabled>
                          {this.localization.get('details.commentOrReport.documentType')}
                        </MenuItem>
                        {getTypeCodesForLanguage(this.props.reportStatusTCValues, this.props.currentLanguage).map(o =>
                        <MenuItem style={{ fontSize: '10pt' }}
                          value={o.typeCodeValue}
                          key={o.typeCodeValue}>{o.translation}
                        </MenuItem>)}
                      </Select>
                    </FormControl>
                  </div>
                  <div className={`${styles.item} ${styles.border}`}>
                    {!this.props.subjectLine && <div className={styles.notValid}></div>}
                    <textarea
                      rows={1}
                      className={styles.input}
                      value={this.props.subjectLine}
                      onChange={this.props.onSubjectChange}
                      disabled={this.props.isOnline}
                      placeholder={this.localization.get('details.commentOrReport.subject')}
                    />
                  </div>
                  <div className={`${styles.itemGrow} ${styles.border}`}>
                    {this.props.reportStatusTC === ReportStatusTC.FreeText &&
                      !this.props.description && <div className={styles.notValid}></div>}
                    <textarea
                      rows={4}
                      className={styles.text}
                      value={this.props.description}
                      onChange={this.props.onDescriptionChange}
                      disabled={this.props.isOnline || this.props.reportStatusTC !== ReportStatusTC.FreeText}
                      placeholder={this.localization.get('details.commentOrReport.description')}
                    />
                  </div>
                </div>
                <div className={styles.column}>
                  <div className={styles.rowGrow}>
                    <div className={`${styles.border}`}>
                      {!this.props.disabled && this.props.AVScanned !== false && (
                        <div className={styles.fileActions} onClick={this.props.onRemoveClick}>
                          <RemoveIcon className={styles.img} style={{ fontSize: '4rem', color: '#84a1d4' }} />
                        </div>
                      )}
                      {(this.props.disabled || this.props.AVScanned === false) && (
                        <div className={styles.noFileActions}>
                          <BlockIcon className={styles.img} style={{ fontSize: '4rem', color: '#84a1d4' }} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`${styles.border} ${styles.rowGrow}`}>{this.fileUploadElement}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dropzone>
    );
  }

  private get fileUploadElement() {
    return !this.uploadButtonEnabled ? (
      <div className={styles.noFileActions} />
    ) : (
      <div className={styles.fileActions} onClick={this.props.onUploadClick}>
        <img
          alt=""
          className={styles.img}
          src={require('../../../../assets/images/save_85x85.png')}
        />
      </div>
    );
  }

  private get attachEnabled() {
    if (this.props.reportStatusTC && this.props.reportStatusTC !== ReportStatusTC.FreeText) {
      return true;
    }
    return false;
  }

  private get uploadButtonEnabled() {
    if (this.props.reportStatusTC && !this.props.isOnline) {
      switch (this.props.reportStatusTC) {
        case ReportStatusTC.FreeText:
          return !!this.props.subjectLine && !!this.props.description;
        case ReportStatusTC.End:
        case ReportStatusTC.EndNoSVS:
        case ReportStatusTC.Temp:
          return !!this.props.url && !!this.props.subjectLine;
        default:
          return false;
      }
    }
    return false;
  }
}
